import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const LoginForm = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!email?.trim()) {
      setError(t('login.emailRequired', 'Email required'));
      return;
    }
    if (!password) {
      setError(t('login.passwordRequired', 'Password required'));
      return;
    }

    setLoading(true);
    try {
      const { data: authData, error: authError } = await supabase?.auth?.signInWithPassword({
        email: email?.trim(),
        password,
      });

      if (authError) {
        setError(authError?.message || t('login.invalidCredentials', 'Invalid credentials'));
        setLoading(false);
        return;
      }

      const userId = authData?.user?.id;
      const { data: profile, error: profileError } = await supabase
        ?.from('profiles')
        ?.select('role, full_name, tenant_id, agency_id, language')
        ?.eq('id', userId)
        ?.single();

      if (profileError || !profile) {
        setError(t('login.profileNotFound', 'Profile not found. Contact your admin.'));
        await supabase?.auth?.signOut();
        setLoading(false);
        return;
      }

      onSuccess(profile);
    } catch (err) {
      setError(t('login.networkError', 'Network error. Try again.'));
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="px-4 py-3 rounded-lg bg-error/10 border border-error/30 text-error text-sm">
          {error}
        </div>
      )}

      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-widest">
          {t('login.emailLabel', 'Email')}
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e?.target?.value)}
          placeholder="you@example.com"
          autoComplete="email"
          className="w-full px-4 py-3 rounded-xl bg-input border border-border text-foreground placeholder-muted-foreground/60 text-base outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>

      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-widest">
          {t('login.passwordLabel', 'Password')}
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e?.target?.value)}
            placeholder="••••••••"
            autoComplete="current-password"
            className="w-full px-4 py-3 pr-12 rounded-xl bg-input border border-border text-foreground placeholder-muted-foreground/60 text-base outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors rtl:left-3 rtl:right-auto"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <EyeIcon open={showPassword} />
          </button>
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          className="w-4 h-4 rounded border-border accent-primary"
        />
        <span className="text-sm text-muted-foreground">
          {t('login.rememberMe', 'Remember me')}
        </span>
      </label>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 font-semibold text-sm tracking-wide transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-elevation-2 hover:shadow-glow-primary"
      >
        {loading ? (
          <>
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {t('login.signingIn', 'Signing in...')}
          </>
        ) : (
          t('login.signIn', 'Sign in')
        )}
      </button>
    </form>
  );
};

export default LoginForm;
